import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { ScopeOrgProvider, useScopeSearch, useScopedUrl } from '../nav/scopeOrg';
import { Navigate, Route, Routes, useNavigate, useParams, useSearchParams } from 'react-router-dom';
import type { Device, ScheduleType } from '@cornerstone/shared';
import { DangerZone } from '../components/DangerZone';
import { API_BASE } from '../client/config';
import { api } from '../client/api';
import { PageContainer } from '../nav/PageContainer';
import { ConfirmModal } from '../components/menu';
import { useAdminRole } from '../nav/useAdminRole';
import { useCanCalibrate } from '../nav/useAuthEmail';
import { useCurrentOrgDeleted } from '../nav/useCurrentOrgDeleted';
import { PairingModal } from '../components/DevicePairing';
import type { PairingCodeIssued } from '@cornerstone/shared';
import { Button, Panel, StatusBadge } from '../components/ui';
import { describeApiError, toast } from '../components/toast';
import { Calibration } from './Calibration';
import { PageHeader } from '../nav/Breadcrumb';
import { DeviceDiagnostics } from '../components/DeviceDiagnostics';
import { DeviceIdleCard } from '../components/DeviceIdleCard';
import { DevicePowerCard } from '../components/DevicePowerCard';
import { DeviceRuntimeBadge, Icon, IconButton, InfoTile, deviceFeatureSet, runtimeStatus } from '../components/DeviceRuntime';
import { DeviceHealthBadges, useServerPlayerBuild } from '../components/DeviceHealth';
import { DeviceStatusRefreshButton, DeviceWifiCard } from '../components/DeviceWifiCard';
import { ContentSkeleton } from '../components/skeleton/ContentSkeleton';
import { cn } from '../utils/cn';
import { deviceCaptureBlockReason, formatNetworkStatus } from '@cornerstone/shared';

// =============================================================================
// Device detail (Figma '디바이스 상세' / '디바이스 추가·수정') — /devices/:id/*
//   • index  상세(읽기 전용): 디바이스 정보(위) + 기기 진단(아래, 이력은 접힘)
//   • edit   추가/수정 폼: ID(disabled)·디바이스명*·유형·그룹·해상도*(1회 지정)
//            + 캘리브레이션 영역(에이치나인 소속만)
//   • calibration  캘리브레이션 워크스페이스(기존, @hnine.com 전용)
//
// 2026-09-14: 구성 관리 > 그룹·디바이스 목록에서는 이 화면을 페이지로 가지 않고
// components/DeviceDetailModal.tsx 모달로 띄운다. 그래서 정보 카드(DeviceInfoCard)·수정 폼
// (DeviceEditForm)·로더(useDeviceRecord)는 라우터에 기대지 않는 조각으로 내보내고, 이 페이지는
// 딥링크(/devices/:id — 스케줄·알림에서 링크)와 캘리브레이션을 위해 남긴다.
// =============================================================================


export function formatDateTime(iso: string | null | undefined): string {
  if (!iso) return '-';
  const d = new Date(iso);
  const p = (n: number) => String(n).padStart(2, '0');
  return `${d.getFullYear()}. ${p(d.getMonth() + 1)}. ${p(d.getDate())} ${p(d.getHours())}:${p(d.getMinutes())}:${p(d.getSeconds())}`;
}

// -----------------------------------------------------------------------------
// 로더 — 기기 한 대 + 할당 타임라인. 스코프·로그인 변화에 이전 정보를 숨긴다.
// -----------------------------------------------------------------------------
export function useDeviceRecord(id: string) {
  const scoped = useScopedUrl();
  const [device, setDevice] = useState<Device | null>(null);
  const [timelines, setTimelines] = useState<ScheduleType[]>([]);
  const [failure, setFailure] = useState<{ id: string; message: string } | null>(null);
  const err = failure?.id === id ? failure.message : null;
  const [reload, setReload] = useState(0);
  const detailRequest = useRef<AbortController | null>(null);
  const hideBlocked = useCallback(() => {
    detailRequest.current?.abort();
    setDevice(null); setTimelines([]); setFailure({ id, message: '기기 접근 권한이 변경되어 이전 정보를 숨겼습니다.' });
  }, [id]);
  useEffect(() => {
    const changed = () => { hideBlocked(); setReload(value => value + 1); };
    const refresh = () => setReload(value => value + 1);
    window.addEventListener('bricklit:scope-changed', changed);
    window.addEventListener('bricklit:auth-changed', changed);
    window.addEventListener('bricklit:logout', hideBlocked);
    window.addEventListener('bricklit:device-changed', refresh);
    // 다른 관리자의 편성·콘텐츠 변경은 권한 변경이 아니다 — 감추지 않고 값만 다시 읽는다.
    window.addEventListener('bricklit:workflow-changed', refresh);
    return () => {
      window.removeEventListener('bricklit:scope-changed', changed);
      window.removeEventListener('bricklit:auth-changed', changed);
      window.removeEventListener('bricklit:logout', hideBlocked);
      window.removeEventListener('bricklit:device-changed', refresh);
      window.removeEventListener('bricklit:workflow-changed', refresh);
    };
  }, [hideBlocked]);

  useEffect(() => {
    if (!id) return;
    const ac = new AbortController();
    detailRequest.current = ac;
    setDevice(null); setTimelines([]); setFailure(null);
    fetch(scoped(`${API_BASE}/api/devices/${id}`), { signal: ac.signal })
      .then((r) => (r.ok ? (r.json() as Promise<Device>) : Promise.reject(new Error(`HTTP ${r.status}`))))
      .then((d) => { if (!ac.signal.aborted) setDevice(d); })
      .catch((e: unknown) => {
        if (!ac.signal.aborted) { ac.abort(); setDevice(null); setTimelines([]); setFailure({ id, message: e instanceof Error ? e.message : String(e) }); }
      });
    void fetch(scoped(`${API_BASE}/api/schedule-types`), { signal: ac.signal })
      .then((r) => (r.ok ? (r.json() as Promise<Awaited<ReturnType<typeof api.listScheduleTypes>>>) : []))
      .then((ts) => { if (!ac.signal.aborted) setTimelines(ts.filter((t) => t.deviceId === id)); })
      .catch(() => undefined);
    return () => ac.abort();
  }, [id, reload]);

  // Route changes render before the previous request effect is cleaned up.
  const current = device?.id === id ? device : null;
  return { device: current, timelines, err, hideBlocked, reload: () => setReload((n) => n + 1) };
}

/** 실제 그룹 배정(device_groups) — 딥링크 페이지용. 구성 관리 모달은 store 값을 props로 넘긴다. */
function useGroupScope(deviceId: string, reloadKey: number) {
  const scoped = useScopedUrl();
  const [groups, setGroups] = useState<Array<{ id: string; name: string }>>([]);
  const [groupId, setGroupId] = useState<string | null>(null);
  useEffect(() => {
    const ac = new AbortController();
    fetch(scoped(`${API_BASE}/api/device-groups`), { credentials: 'include', signal: ac.signal })
      .then((r) => (r.ok ? r.json() as Promise<{ groups: Array<{ id: string; name: string }>; devices: Array<{ id: string; groupId: string | null }> }> : Promise.reject(new Error(String(r.status)))))
      .then((scope) => { if (ac.signal.aborted) return; setGroups(scope.groups.map((g) => ({ id: g.id, name: g.name }))); setGroupId(scope.devices.find((d) => d.id === deviceId)?.groupId ?? null); })
      .catch(() => undefined);
    return () => ac.abort();
  }, [deviceId, reloadKey]);
  const assign = async (next: string | null): Promise<boolean> => {
    const r = await fetch(scoped(`${API_BASE}/api/device-groups/devices/${encodeURIComponent(deviceId)}`), { method: 'PUT', credentials: 'include',
      headers: { 'Content-Type': 'application/json' }, body: JSON.stringify({ groupId: next }) });
    if (r.ok) setGroupId(next);
    return r.ok;
  };
  return { groups, groupId, assign };
}

/**
 * /devices/:id — 운영사 운영자는 경보 메일·알림 링크(?orgId=<고객사>)로 들어온다(2026-10-06 대교: 백오피스 계정으로
 * 메일의 '기기 상세'를 누르면 403). 쿼리의 orgId 를 범위로 걸어 안쪽 호출이 전부 그 고객사 컨텍스트로 가게 한다.
 * 고객사 Owner 는 쿼리가 없거나 자기 조직이라 아무 차이가 없다.
 */
export function DeviceDetail(): JSX.Element {
  const [params] = useSearchParams();
  return <ScopeOrgProvider orgId={params.get('orgId')}><DeviceDetailPage /></ScopeOrgProvider>;
}

function DeviceDetailPage(): JSX.Element {
  const { id = '' } = useParams<{ id: string }>();
  const [params] = useSearchParams();
  const search = useScopeSearch();
  const record = useDeviceRecord(id);
  const [scopeKey, setScopeKey] = useState(0);
  const scope = useGroupScope(id, scopeKey);

  // 캘리브레이션 진입은 지정 계정(+로컬 dev) 전용 — 버튼 숨김 + 라우트 직접 접근도 차단.
  const { canCalibrate, loading: authLoading } = useCanCalibrate();
  const base = `/devices/${id}`;

  return (
    <PageContainer variant="full" className="flex flex-col overflow-hidden bg-bg text-text">
      <Routes>
        <Route
          index
          element={<DetailView device={record.device} timelines={record.timelines} err={record.err} onScopeBlocked={record.hideBlocked}
            groupName={scope.groups.find((g) => g.id === scope.groupId)?.name ?? null} />}
        />
        <Route
          path="edit"
          element={record.device ? <EditView device={record.device} onSaved={() => { record.reload(); setScopeKey((n) => n + 1); }}
            groups={scope.groups} groupId={scope.groupId} onAssignGroup={scope.assign} />
            : <DeviceEntryPlaceholder leaf={params.get('new') === '1' ? '디바이스 추가' : '디바이스 수정'} detailId={id} variant="form" err={record.err} />}
        />
        <Route
          path="calibration"
          element={
            record.err ? <DeviceEntryPlaceholder leaf="캘리브레이션" detailId={id} variant="split" err={record.err} />
            : authLoading || !record.device ? <DeviceEntryPlaceholder leaf="캘리브레이션" detailId={id} variant="split" /> : canCalibrate ? (
              <Calibration deviceId={id} />
            ) : (
              <Navigate to={`${base}${search}`} replace />
            )
          }
        />
        <Route path="*" element={<Navigate to={`${base}${search}`} replace />} />
      </Routes>
    </PageContainer>
  );
}

// -----------------------------------------------------------------------------
// 공통 레이아웃 조각
// -----------------------------------------------------------------------------

function Breadcrumb({ leaf }: { leaf: string; detailId?: string }) {
  return (
    <div className="px-6 pt-6">
      <PageHeader className="mb-0" items={[{ label: leaf }]} />
    </div>
  );
}

function DeviceEntryPlaceholder({ leaf, detailId, variant, err }: {
  leaf: string; detailId?: string; variant: 'cards' | 'form' | 'split'; err?: string | null;
}) {
  return <div className="relative h-full overflow-auto pb-10">
    <Breadcrumb leaf={leaf} {...(detailId ? { detailId } : {})} />
    <div className="px-6 pt-4">
      {err ? <p role="alert" className="rounded border border-red/50 bg-red/15 px-3 py-2 text-[14px] text-red">{err}</p>
        : <ContentSkeleton variant={variant} label={`${leaf} 정보를 불러오는 중…`} />}
    </div>
  </div>;
}

/** 새 편성을 왜 아직 못 받았는지 한 줄로 — 진행 파일 수, 받는 파일, 마지막 실패, 재시도 상태. */
export function formatDownloadStatus(status: Device['downloadStatus']): string {
  if (!status) return '보고 대기';
  const mb = (n: number) => `${Math.round(n / 1024 ** 2)}`;
  return [
    `${status.filesDone}/${status.filesTotal} 파일 완료`,
    status.current ? `받는 중 ${status.current.name} ${mb(status.current.received)}${status.current.total !== null ? `/${mb(status.current.total)}` : ''} MB` : null,
    status.lastFailure ? `마지막 실패 ${formatDateTime(status.lastFailure.at)} ${status.lastFailure.reason} (${status.lastFailure.name})` : null,
    status.updateFailures > 0 ? `업데이트 재시도 ${status.updateFailures}회${status.retriesExhausted ? ' · 소진(재연결 대기)' : ''}` : null,
    status.bundleRestore ? '복원 편성 재생 중' : null,
    typeof status.filesBytes === 'number' ? `파일 합계 ${formatBytes(status.filesBytes)}` : null,
    status.verifiedAt ? `검증 ${formatDateTime(status.verifiedAt)}` : null,
    typeof status.bundleBuckets === 'number' ? `복원 편성 버킷 ${status.bundleBuckets}${status.bundleBuckets >= 2 ? ' (옛 복사본 남음)' : ''}` : null,
    status.bundle ? `복원 편성 ${status.bundle.format === null ? '없음' : `format ${status.bundle.format}${status.bundle.format === 1 ? ' (원본 복사본)' : ''}`}`
      + (status.bundle.lastSaveAt ? ` · 저장 시도 ${formatDateTime(status.bundle.lastSaveAt)} → ${status.bundle.lastSaveResult ?? '?'}` : '')
      + (status.bundle.gate ? ` · 저장 대기: ${status.bundle.gate}` : '') : null,
    status.storage?.details ? `저장소 ${Object.entries(status.storage.details).map(([k, n]) => `${k} ${formatBytes(n)}`).join(' / ')}` : null,
    status.storage?.buckets?.length ? `버킷 ${status.storage.buckets.map(b => `${b.name.replace(/^bricklit-/, '')} ${b.entries}개 ${formatBytes(b.bytes)}`).join(' / ')}` : null,
    status.lastScopeClear ? `마지막 초기화 ${formatDateTime(status.lastScopeClear.at)} · 사유 ${status.lastScopeClear.reason}` : null,
  ].filter(Boolean).join(' · ');
}

function formatBytes(value: number | null | undefined, suffix = ''): string {
  if (value === null || value === undefined) return '-';
  return value >= 1024 ** 3 ? `${(value / 1024 ** 3).toFixed(2)} GB${suffix}` : `${Math.round(value / 1024 ** 2)} MB${suffix}`;
}

/** 전원 카드는 HIKVISION APK 로 분류된 기기에만 의미가 있다. 분류 전(옛 APK)은 SDK 보고로 판단한다. */
export function showsPowerCard(device: Device): boolean {
  if (device.runtime && device.runtime.deviceClass !== 'unknown') return deviceFeatureSet(device.runtime).power || device.runtime.deviceClass === 'hikvision_apk';
  return device.powerCapable === true || device.nativeStatus?.sdkAvailable === true;
}

/** 삭제 예정 조직 안내 — 상세·모달 공용. */
export function OrgDeletedNotice({ className }: { className?: string }): JSX.Element {
  return (
    <div className={cn('flex items-center gap-2 rounded-md border border-blue/30 bg-blue-soft px-4 py-3 text-[13px] text-blue', className)}>
      <span aria-hidden>ⓘ</span>
      삭제 예정인 조직의 디바이스는 수정 또는 삭제가 불가합니다.
    </div>
  );
}

// -----------------------------------------------------------------------------
// 디바이스 정보 카드(읽기 전용) — 페이지·모달 공용. 라우팅은 콜백으로.
// -----------------------------------------------------------------------------
export function DeviceInfoCard({
  device,
  timelines,
  err,
  readOnly = false,
  groupName = null,
  onEdit,
  onDeleted,
  className,
}: {
  device: Device | null;
  timelines: ScheduleType[];
  err: string | null;
  /** 실제 배정 그룹 이름(device_groups). 없으면 '미배정'. 옛 groupLabel은 보이지 않는다. */
  groupName?: string | null;
  /** 삭제 예정 조직 등 — 수정·삭제·명령 버튼을 숨긴다. */
  readOnly?: boolean;
  onEdit: () => void;
  onDeleted: () => void;
  className?: string;
}): JSX.Element {
  const scoped = useScopedUrl();
  const [deleteOpen, setDeleteOpen] = useState(false);
  const [blockedOpen, setBlockedOpen] = useState(false);
  const adminRole = useAdminRole();
  const isBo = adminRole === 'h9_super';
  const [unlocking, setUnlocking] = useState(false);

  const requestDelete = () => {
    if (timelines.length > 0) setBlockedOpen(true);
    else setDeleteOpen(true);
  };

  const unlockRuntime = async () => {
    if (!device || unlocking) return;
    setUnlocking(true);
    try {
      const r = await fetch(scoped(`${API_BASE}/api/devices/${encodeURIComponent(device.id)}/runtime/reset`), { method: 'POST' });
      if (!r.ok) throw new Error(`HTTP ${r.status}`);
      toast('현재 하드웨어 정보를 기준으로 잠금을 해제했습니다.', 'success');
      window.dispatchEvent(new Event('bricklit:device-changed'));
    } catch (error) { toast(describeApiError(error), 'error'); }
    finally { setUnlocking(false); }
  };

  const runtime = device ? runtimeStatus(device) : null;
  const features = deviceFeatureSet(device?.runtime);
  const native = device?.nativeStatus ?? null;
  const serverBuild = useServerPlayerBuild();

  return (
    <div className={cn('border border-line rounded-md bg-panel', className)}>
      <div className="flex items-center justify-between px-5 py-3 border-b border-line">
        <div className="flex min-w-0 items-center gap-3">
          <h2 className="text-[15px] font-semibold">디바이스 정보</h2>
          {device && <DeviceRuntimeBadge device={device} />}
          {device && <DeviceHealthBadges device={device} serverBuild={serverBuild} compact />}
        </div>
        {!readOnly && device && (
          <div className="flex flex-wrap justify-end gap-2">
            <Button variant="danger" onClick={requestDelete}>삭제</Button>
            <Button variant="primary" onClick={onEdit}>수정</Button>
          </div>
        )}
      </div>

      {err && (
        <div role="alert" className="m-4 rounded border border-red/50 bg-red/15 px-3 py-2 text-[14px] text-red">
          디바이스 정보를 불러오지 못했습니다: {err}
        </div>
      )}

      {device && (
        <div className="grid gap-3 p-4 md:grid-cols-2">
          <InfoTile title="기본 정보" icon="panel" rows={[
            { label: '디바이스 ID', value: device.id, mono: true },
            { label: '디바이스명', value: device.name },
            { label: '그룹', value: groupName ?? '미배정' },
            { label: '해상도', value: device.resolutionW && device.resolutionH ? `${device.resolutionW} × ${device.resolutionH}` : '자동' },
            { label: '타임라인', value: timelines.length ? timelines.map((t) => t.name).join(', ') : '-' },
          ]} />
          <InfoTile title="운영 환경" icon={runtime?.deviceClass === 'web_browser' ? 'globe' : runtime?.deviceClass === 'android_apk' ? 'android' : 'panel'}
            trailing={runtime?.locked && isBo && !readOnly
              ? <Button className="!px-2.5 !py-1 !text-[12px]" disabled={unlocking} onClick={() => void unlockRuntime()} title="현재 보고된 제조사·모델·시리얼을 등록 기준으로 다시 인정합니다.">잠금 해제</Button>
              : undefined}
            rows={[
              { label: '확인 상태', value: runtime?.locked ? '원격 제어 잠김' : runtime?.status === 'confirmed' ? '확인됨 · 기기 보고 기준'
                : runtime?.status === 'provisional' ? '확인 중 · 제조사·모델 보고 대기(APK 0.4.8 이상)' : runtime?.status === 'stale' ? '오래됨 · 최근 15분 내 보고 없음' : '미확인 · 아직 보고가 없습니다' },
              ...(features.appVersion || device.appVersion ? [{ label: '앱 버전', value: device.appVersion ? `v${device.appVersion}` : '미확인' }] : []),
              { label: '웹 실행본', value: native?.webBundle ? `${native.webBundle.replace('assets/', '')}${serverBuild?.bundle && serverBuild.bundle !== native.webBundle ? ` · 서버 ${serverBuild.bundle.replace('assets/', '')} (구버전)` : serverBuild?.bundle ? ' · 최신' : ''}` : '보고 대기' },
              ...(native?.webViewVersion ? [{ label: 'WebView', value: native.webViewVersion }] : []),
              ...(runtime?.deviceClass !== 'web_browser' ? [
                { label: '제조사', value: device.runtime?.hardwareManufacturer ?? '보고 대기' },
                { label: '모델', value: device.runtime?.hardwareModel ?? '보고 대기' },
                { label: '시리얼', value: device.runtime?.hardwareSerial ?? native?.serial ?? '보고 대기', mono: true },
              ] : []),
              { label: '마지막 확인', value: formatDateTime(device.runtime?.capabilitiesReportedAt ?? device.nativeReportedAt ?? device.lastSeenAt) },
            ]}>
            {runtime?.locked && <p className="mt-3 rounded border border-red/40 bg-red/10 px-2.5 py-2 text-[12px] leading-relaxed text-red">
              등록 시점의 제조사·모델·시리얼과 다른 값이 보고돼 전원·재시작·화면 캡처·무음 업데이트를 잠갔습니다. 기기 교체가 맞다면 BO 계정으로 잠금을 해제하세요.
            </p>}
          </InfoTile>
          <InfoTile title="시스템 상태" icon="shield" trailing={<DeviceStatusRefreshButton device={device} disabled={readOnly} />} rows={native ? [
            { label: '시간대', value: native.timezone ? `${native.timezone}${typeof native.rtcOffsetMs === 'number' ? ` · RTC 오차 ${Math.round(native.rtcOffsetMs / 1000)}초` : ''}` : '-' },
            ...(runtime?.deviceClass === 'hikvision_apk' ? [
              { label: 'SDK', value: `${native.sdkVersion ?? '-'}${native.systemVersion ? ` · 펌웨어 ${native.systemVersion}` : ''}` },
            ] : []),
            // 홈 고정은 Android 기본 홈 역할이 담당한다. 공급사 launcherApp 값은
            // 이 펌웨어에서 항상 null이라 참고용으로만 덧붙인다.
            { label: '홈 고정', value: native.isDefaultHome === undefined ? '-'
              : native.isDefaultHome ? '기본 홈 지정됨' : '지정 안 됨 · 부팅 후 다른 앱이 뜰 수 있음' },
            { label: '자가 복구', value: native.canSelfRecover === undefined ? '-'
              : native.canSelfRecover ? '가능 · 가려지면 45초 내 복귀' : '불가 · 다른 앱 위에 표시 권한 필요' },
            { label: '시스템 언어', value: native.locale
              ? `${native.locale}${native.bootLocale && native.bootLocale !== native.locale ? ` · 재시작 후 ${native.bootLocale}` : ''}` : '-' },
            { label: 'WebView', value: native.webViewVersion ?? '-' },
            { label: 'Player 웹 실행본', value: native.webBundleVersion ? `${native.webBundleVersion} (${native.webBundleSource === 'apk' ? 'APK 내장' : '다운로드 캐시'})` : '보고 대기', mono: true },
            { label: '업데이트 상태', value: native.updateStatus ?? '-' },
            { label: '온도·부하', value: [native.temperature ? `${native.temperature}℃` : null,
              native.cpuUsage ? `CPU ${native.cpuUsage.replace(/^cpu:/, '')}%` : null,
              typeof native.memoryUsed === 'number' && typeof native.memoryTotal === 'number'
                ? `RAM ${native.memoryUsed} / ${native.memoryTotal} MB` : null].filter(Boolean).join(' · ') || '-' },
            { label: '저장소', value: typeof native.storageFreeBytes === 'number' && typeof native.storageTotalBytes === 'number'
              ? `${(native.storageFreeBytes / 1024 ** 3).toFixed(1)} / ${(native.storageTotalBytes / 1024 ** 3).toFixed(1)} GB 여유` : '-' },
            { label: '네트워크', value: formatNetworkStatus(native) ?? '보고 대기 · APK 0.5.10 이상' },
            ...(runtime?.deviceClass === 'hikvision_apk' ? [
              { label: '화면', value: [native.resolution ?? null,
                typeof native.inputSource === 'number' ? `입력 ${native.inputSource === 1 ? 'Android' : `소스 ${native.inputSource}`}` : null,
                typeof native.backlight === 'number' ? `백라이트 ${native.backlight}` : null].filter(Boolean).join(' · ') || '-' },
            ] : []),
            { label: '오프라인 캐시', value: formatBytes(device.cacheBytes, ' 선반입') },
            { label: '콘텐츠 다운로드', value: formatDownloadStatus(device.downloadStatus) },
          ] : [
            { label: '오프라인 캐시', value: formatBytes(device.cacheBytes, ' 선반입') },
            { label: '콘텐츠 다운로드', value: formatDownloadStatus(device.downloadStatus) },
            { label: '네이티브 상태', value: runtime?.deviceClass === 'web_browser' ? '웹 브라우저는 보고하지 않습니다' : '보고 대기' },
          ]} />
          <DeviceWifiCard device={device} canPin={isBo && !readOnly} />
          <InfoTile title="이력" icon="refresh" rows={[
            { label: '최초 등록', value: formatDateTime(device.pairedAt ?? device.createdAt) },
            { label: '마지막 수정', value: device.updatedAt ? `${formatDateTime(device.updatedAt)}${device.updatedBy ? ` · ${device.updatedBy}` : ''}` : '-' },
            { label: '마지막 접속', value: formatDateTime(device.lastSeenAt) },
          ]} />
        </div>
      )}

      {blockedOpen && (
        <ConfirmModal
          title="알림"
          message={'할당된 타임라인이 있는 디바이스는 삭제할 수 없습니다.\n타임라인 삭제 후 다시 시도해 주세요.'}
          confirmLabel="확인"
          danger={false}
          hideCancel
          onCancel={() => setBlockedOpen(false)}
          onConfirm={() => setBlockedOpen(false)}
        />
      )}
      {deleteOpen && device && (
        <ConfirmModal
          title="알림"
          message={'삭제한 내용은 복구할 수 없습니다.\n삭제하시겠습니까?'}
          confirmLabel="확인"
          danger={false}
          onCancel={() => setDeleteOpen(false)}
          onConfirm={async () => {
            const r = await fetch(scoped(`${API_BASE}/api/devices/${device.id}`), { method: 'DELETE' });
            if (!r.ok) throw new Error(`HTTP ${r.status}`);
            toast('삭제되었습니다.', 'success');
            onDeleted();
          }}
        />
      )}
    </div>
  );
}

function DeviceScreenshot({ device, readOnly }: { device: Device; readOnly: boolean }): JSX.Element {
  const scoped = useScopedUrl();
  const [image, setImage] = useState<{ url: string; capturedAt: string | null; receivedAt: string | null } | null>(null);
  const [requesting, setRequesting] = useState(false);
  const activeRef = useRef(true);
  const imageUrlRef = useRef<string | null>(null);
  const pollRef = useRef<number | null>(null);
  const load = useCallback(async (): Promise<number | null> => {
    try {
      const response = await fetch(scoped(`${API_BASE}/api/devices/${encodeURIComponent(device.id)}/screenshot`), { cache: 'no-store' });
      if (!response.ok || !activeRef.current) return null;
      const blob = await response.blob();
      if (!activeRef.current || typeof URL.createObjectURL !== 'function') return null;
      const url = URL.createObjectURL(blob);
      if (imageUrlRef.current) URL.revokeObjectURL(imageUrlRef.current);
      imageUrlRef.current = url;
      const receivedAt = response.headers.get('x-received-at');
      setImage({ url, capturedAt: response.headers.get('x-captured-at'), receivedAt });
      const parsed = Date.parse(receivedAt ?? '');
      return Number.isFinite(parsed) ? parsed : null;
    } catch { return null; }
  }, [device.id]);
  useEffect(() => {
    activeRef.current = true;
    void load();
    const timer = window.setInterval(() => void load(), 30_000);
    return () => {
      activeRef.current = false;
      window.clearInterval(timer);
      if (pollRef.current !== null) window.clearTimeout(pollRef.current);
      if (imageUrlRef.current) URL.revokeObjectURL(imageUrlRef.current);
      imageUrlRef.current = null;
    };
  }, [load]);
  const requestCapture = async () => {
    if (requesting || readOnly || deviceCaptureBlockReason(device) !== null) return;
    setRequesting(true);
    try {
      await api.sendDeviceCommand(device.id, 'captureScreen');
      toast('화면 캡처를 요청했습니다.', 'success');
      const requestedAt = Date.now();
      const poll = async (remaining: number) => {
        const receivedAt = await load();
        if ((receivedAt !== null && receivedAt >= requestedAt) || remaining <= 1 || !activeRef.current) { setRequesting(false); return; }
        pollRef.current = window.setTimeout(() => void poll(remaining - 1), 3_000);
      };
      pollRef.current = window.setTimeout(() => void poll(10), 3_000);
    } catch (error) {
      setRequesting(false);
      toast(describeApiError(error), 'error');
    }
  };
  const captureBlocked = deviceCaptureBlockReason(device);
  return <InfoTile title="현재 화면" icon="camera" className="flex min-w-0 flex-col"
    trailing={<IconButton label={requesting ? '캡처 확인 중' : '화면 캡처'} icon="camera" tone="primary" size="sm" disabled={readOnly || requesting || captureBlocked !== null}
      title={captureBlocked ?? '현재 기기 화면을 한 번 캡처합니다.'} onClick={() => void requestCapture()} className={requesting ? 'animate-pulse' : ''} />}>
    {image ? <div className="min-w-0"><img src={image.url} alt="최근 기기 화면 캡처" className="max-h-[420px] w-full rounded border border-line bg-black object-contain" />
      <div className="mt-1.5 text-[12px] text-text-dim">기기 촬영 {formatDateTime(image.capturedAt)}{image.receivedAt ? ` · 서버 수신 ${formatDateTime(image.receivedAt)}` : ''}</div></div>
      : <div className="flex min-h-36 items-center justify-center rounded border border-dashed border-line bg-panel text-[13px] text-text-dim">아직 캡처된 화면이 없습니다.</div>}
    <p className="mt-2 text-[12px] text-text-dim">{captureBlocked ?? '요청할 때 한 번만 캡처하며 최근 10장을 보관합니다.'}</p>
  </InfoTile>;
}

type UpdateRequest = { id: string; deviceId: string; targetVersion: string; status: string; requestedAt: string; expiresAt: string | null; result: string | null };
const UPDATE_STATUS_LABEL: Record<string, string> = { pending: '승인 대기', approved: '승인됨', installing: '설치 중', success: '업데이트 완료', failed: '실패', rejected: '거절됨', expired: '승인 만료' };
function DeviceUpdateRequests({ device, readOnly }: { device: Device; readOnly: boolean }): JSX.Element {
  const scoped = useScopedUrl();
  const [items, setItems] = useState<UpdateRequest[]>([]);
  const load = useCallback(() => fetch(scoped(`${API_BASE}/api/devices/update-requests`)).then(r => r.ok ? r.json() as Promise<UpdateRequest[]> : []).then(rows => setItems(rows.filter(row => row.deviceId === device.id))).catch(() => undefined), [device.id]);
  useEffect(() => { void load(); const timer = window.setInterval(() => void load(), 30_000); return () => window.clearInterval(timer); }, [load]);
  const decide = async (id: string, action: 'approve' | 'reject') => {
    const response = await fetch(scoped(`${API_BASE}/api/devices/update-requests/${encodeURIComponent(id)}/${action}`), { method: 'POST' });
    if (!response.ok) { toast('업데이트 요청을 처리하지 못했습니다.', 'error'); return; }
    toast(action === 'approve' ? '업데이트를 승인했습니다.' : '업데이트 요청을 거절했습니다.', 'success'); void load();
  };
  const channel = device.updateChannel === 'canary' ? '시험 채널' : device.updateChannel === 'paused' ? '업데이트 중지' : '정식 채널';
  return <InfoTile title="앱 업데이트" icon="download" className="flex min-w-0 flex-col"
    trailing={<span className="text-[12px] text-text-dim">v{device.appVersion ?? '미확인'} · {channel}{device.desiredAppVersion ? ` · 목표 v${device.desiredAppVersion}` : ''}</span>}>
    {!items.length ? <div className="rounded border border-dashed border-line bg-panel px-3 py-5 text-center text-[13px] text-text-dim">승인 요청이 없습니다. 새 버전이 배포되면 기기가 승인을 요청합니다.</div>
      : <div className="flex flex-col gap-2">{items.slice(0, 5).map(item => <div key={item.id} className="flex flex-wrap items-center gap-2 rounded border border-line bg-panel px-3 py-2 text-[13px]">
      <span className="font-medium">v{item.targetVersion}</span><span className="text-text-dim">{UPDATE_STATUS_LABEL[item.status] ?? item.status}</span><span className="text-text-dim">{formatDateTime(item.requestedAt)}</span>
      {item.status === 'pending' && <div className="ml-auto flex gap-1.5">
        <Button variant="primary" className="!px-2.5 !py-1 !text-[12px]" disabled={readOnly} onClick={() => void decide(item.id, 'approve')}>승인</Button>
        <Button className="!px-2.5 !py-1 !text-[12px]" disabled={readOnly} onClick={() => void decide(item.id, 'reject')}>거절</Button></div>}
    </div>)}</div>}
    <p className="mt-2 text-[12px] text-text-dim">승인하면 HIKVISION SDK로 무음 설치하고, 새 버전이 보고되면 완료로 표시합니다.</p>
  </InfoTile>;
}

/** 일반 Android APK — 무음 설치 경로가 없어 수동 설치 안내만 보인다. */
function DeviceManualUpdate({ device }: { device: Device }): JSX.Element {
  const scoped = useScopedUrl();
  const [latest, setLatest] = useState<{ versionName?: string; versionCode?: number; url?: string } | null>(null);
  useEffect(() => {
    const channel = device.updateChannel === 'canary' ? 'stationc-player-canary.json' : 'stationc-player-latest.json';
    fetch(scoped(`${API_BASE}/device-test/${channel}`), { cache: 'no-store' }).then(r => r.ok ? r.json() as Promise<{ versionName?: string; versionCode?: number; url?: string }> : null)
      .then(setLatest).catch(() => setLatest(null));
  }, [device.updateChannel]);
  const current = device.appVersion ?? null;
  const newer = latest?.versionName && current && latest.versionName !== current
    && latest.versionName.split('.').map(Number).some((v, i) => v !== (current.split('.').map(Number)[i] ?? 0) && v > (current.split('.').map(Number)[i] ?? 0));
  return <InfoTile title="앱 업데이트" icon="download" className="flex min-w-0 flex-col"
    trailing={<span className="text-[12px] text-text-dim">v{current ?? '미확인'}</span>}>
    <div className="rounded border border-dashed border-line bg-panel px-3 py-4 text-[13px] text-text-dim">
      {newer ? <>새 버전 <span className="font-medium text-text">v{latest?.versionName}</span>이 있습니다. 이 기기는 HIKVISION 패널이 아니어서 현장에서 APK를 직접 설치해야 합니다.</>
        : latest?.versionName ? <>배포 중인 최신 버전은 v{latest.versionName}입니다. 무음 설치는 HIKVISION 패널에서만 지원됩니다.</> : '배포 정보를 불러오지 못했습니다.'}
    </div>
    {latest?.url && <a href={latest.url} className="mt-2 inline-flex items-center gap-1.5 text-[12px] text-accent hover:underline" target="_blank" rel="noreferrer"><Icon name="download" size={13} />APK 내려받기</a>}
  </InfoTile>;
}

export function DeviceOperationsCard({ device, readOnly = false, className }: { device: Device; readOnly?: boolean; className?: string }): JSX.Element {
  const adminRole = useAdminRole();
  const isBo = adminRole === 'h9_super';
  const commandDisabled = readOnly || !device.online;
  const features = deviceFeatureSet(device.runtime);
  const { deviceClass } = runtimeStatus(device);
  // 옛 APK(0.4.7 이하)는 분류 전이라 SDK 보고로 판단한다.
  const legacyHik = deviceClass === 'unknown' && device.nativeStatus?.sdkAvailable === true;
  const showCapture = isBo && (features.screenCapture || legacyHik);
  const showApprovals = isBo && (features.updateRequests || legacyHik);
  const showManual = isBo && !showApprovals && (features.manualUpdate || (deviceClass === 'unknown' && !!device.appVersion && !legacyHik));
  const command = (name: 'reload' | 'identify', success: string) => api.sendDeviceCommand(device.id, name)
    .then(() => toast(success, 'success')).catch(error => toast(describeApiError(error), 'error'));
  const offline = '오프라인 기기에는 명령을 보낼 수 없습니다.';
  return <Panel className={className ?? ''} title="기기 운영" bodyClassName="p-4"
    trailing={<div className="flex items-center gap-2">
      <IconButton label="플레이어 새로고침" icon="refresh" size="sm" disabled={commandDisabled} title={device.online ? '플레이어 화면을 새로 불러옵니다. 서버에 새 플레이어 버전이 있으면 그 버전으로 교체합니다.' : offline}
        onClick={() => void command('reload', '새로고침 명령을 보냈습니다.')} />
      <IconButton label="화면 식별" icon="identify" size="sm" disabled={commandDisabled} title={device.online ? '현장 화면에 식별 배지를 4초간 표시합니다.' : offline}
        onClick={() => void command('identify', '화면 식별 명령을 보냈습니다.')} />
      <StatusBadge tone={device.online ? 'green' : 'dim'}>{device.online ? '온라인' : '오프라인'}</StatusBadge>
    </div>}>
    <div className="flex flex-col gap-3">
      {(showCapture || showApprovals || showManual) ? <div className="grid gap-3 xl:grid-cols-2">
        {showCapture && <DeviceScreenshot device={device} readOnly={readOnly} />}
        {showApprovals && <DeviceUpdateRequests device={device} readOnly={readOnly} />}
        {showManual && <DeviceManualUpdate device={device} />}
      </div> : <p className="text-[12px] text-text-dim">
        {deviceClass === 'web_browser' ? '웹 브라우저 플레이어는 새로고침·화면 식별·진단만 지원합니다.' : '원격 명령은 기기가 다음 확인 주기에 실행합니다.'}
      </p>}
    </div>
  </Panel>;
}

// -----------------------------------------------------------------------------
// 상세 페이지(읽기 전용) — 정보 카드가 위, 진단(이력 접힘)이 아래.
// -----------------------------------------------------------------------------
function DetailView({
  device,
  timelines,
  err,
  onScopeBlocked,
  groupName,
}: {
  device: Device | null;
  timelines: ScheduleType[];
  err: string | null;
  onScopeBlocked: () => void;
  groupName: string | null;
}) {
  const search = useScopeSearch();
  const navigate = useNavigate();
  const orgDeleted = useCurrentOrgDeleted();

  if (!device && !err) return <DeviceEntryPlaceholder leaf="디바이스 상세" variant="cards" />;

  return (
    <div className="relative h-full overflow-auto pb-10">
      <Breadcrumb leaf="디바이스 상세" />
      {/* 삭제 예정 조직의 디바이스는 Only Read — 안내 배너 + 수정·삭제 숨김. */}
      {orgDeleted && <OrgDeletedNotice className="mx-6 mt-4" />}
      <DeviceInfoCard className="mx-6 mt-4" device={device} timelines={timelines} err={err} readOnly={orgDeleted} groupName={groupName}
        onEdit={() => navigate(`edit${search}`)} onDeleted={() => navigate('/org')} />
      {device && !err && <DeviceOperationsCard className="mx-6 mt-4" device={device} readOnly={orgDeleted} />}
      {device && !err && <DeviceIdleCard key={device.id} className="mx-6 mt-4" device={device} readOnly={orgDeleted} />}
      {device && !err && showsPowerCard(device) && <DevicePowerCard className="mx-6 mt-4" device={device} readOnly={orgDeleted} />}
      {device && !err && !showsPowerCard(device) && device.runtime?.deviceClass === 'web_browser' && (
        <section role="note" aria-label="브라우저 기기 전원 안내" className="mx-6 mt-4 rounded-md border border-line bg-panel-2 px-5 py-4 text-[13px] text-text-dim">
          <p className="font-medium text-text">브라우저 기기 — 전원 제어와 전원 스케줄은 적용되지 않습니다.</p>
          <p className="mt-1">화면 끄기/켜기 명령은 재생 화면을 검게 가리는 방식으로 대체되고, 실제 절전·재부팅은 기기 OS(kiosk 설정)에서 관리해야 합니다. 설치 체크리스트: docs/BROWSER_DEVICE_SETUP.md</p>
        </section>
      )}
      {device && !err && <DeviceDiagnostics className="mx-6 mt-4" deviceId={device.id} onScopeBlocked={onScopeBlocked} />}
    </div>
  );
}

// -----------------------------------------------------------------------------
// 추가/수정 폼 — 페이지·모달 공용. isNew면 '디바이스 추가'(페어링 직후), 아니면 '디바이스 수정'.
// -----------------------------------------------------------------------------
export function DeviceEditForm({
  device,
  isNew = false,
  groups,
  groupId = null,
  onAssignGroup,
  onSaved,
  onCancel,
  onCalibrate,
  onDeleted,
  className,
}: {
  device: Device;
  isNew?: boolean;
  /** 실제 그룹 배정 선택지. 주면 '그룹' 셀렉트가 보이고 저장 시 onAssignGroup으로 배정을 바꾼다(옛 자유입력 groupLabel 대체, D2). */
  groups?: Array<{ id: string; name: string }>;
  groupId?: string | null;
  onAssignGroup?: ((groupId: string | null) => Promise<boolean>) | undefined;
  /** 저장 성공 뒤(토스트는 폼이 띄운다). */
  onSaved: () => void;
  /** 취소 확인 뒤. */
  onCancel: () => void;
  /** 캘리브레이션 화면으로 — 페이지 라우트라 호출자가 이동을 맡는다. */
  onCalibrate?: () => void;
  /** 삭제 성공 뒤. 주면 폼 맨 아래에 위험 구역이 보인다(2026-09-18 대교). */
  onDeleted?: () => void;
  className?: string;
}): JSX.Element {
  const scoped = useScopedUrl();
  // 캘리브레이션 진입은 지정 계정(+로컬 dev) 전용으로 노출.
  const { canCalibrate } = useCanCalibrate();
  // 관리자 등급 — 최고 관리자(H9·FO)만 해상도 재수정·디바이스 갱신 가능.
  const adminRole = useAdminRole();
  const isSuper = adminRole === 'h9_super' || adminRole === 'fo_super';
  const isBo = adminRole === 'h9_super';

  const [name, setName] = useState('');
  const [groupPick, setGroupPick] = useState<string>(groupId ?? '');
  const [resW, setResW] = useState('');
  const [resH, setResH] = useState('');
  const [updateChannel, setUpdateChannel] = useState<'stable' | 'canary' | 'paused'>('stable');
  const [desiredAppVersion, setDesiredAppVersion] = useState('');
  const [saving, setSaving] = useState(false);
  const [cancelOpen, setCancelOpen] = useState(false);
  const [deleteOpen, setDeleteOpen] = useState(false);
  const [renewOpen, setRenewOpen] = useState(false);
  const renewIssuedAtRef = useRef(0);
  const [error, setError] = useState<string | null>(null);

  // 디바이스 로드 시 폼 초기화. 페어링 직후 placeholder 이름('새 디바이스')은 비워서
  // 사용자가 실제 이름을 입력하게 한다.
  useEffect(() => {
    setName(isNew && device.name === '새 디바이스' ? '' : device.name);
    setResW(device.resolutionW?.toString() ?? '');
    setResH(device.resolutionH?.toString() ?? '');
    setUpdateChannel(device.updateChannel ?? 'stable');
    setDesiredAppVersion(device.desiredAppVersion ?? '');
  }, [device, isNew]);

  useEffect(() => { setGroupPick(groupId ?? ''); }, [groupId]);

  // Figma: 한번 지정한 해상도는 수정 불가 — 디바이스가 이미 해상도를 가지면 잠금.
  // 단, 최고 관리자(BO)는 변경 가능.
  const resolutionLocked = !!(device.resolutionW && device.resolutionH) && !isSuper;
  const numericOk = /^\d+$/.test(resW.trim()) && /^\d+$/.test(resH.trim());
  const valid = name.trim().length > 0 && numericOk;

  const save = async () => {
    if (!valid || saving) return;
    setSaving(true);
    setError(null);
    try {
      const r = await fetch(scoped(`${API_BASE}/api/devices/${device.id}/profile`), {
        method: 'PATCH',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          name: name.trim(),
          resolutionW: Number(resW),
          resolutionH: Number(resH),
          ...(isBo ? { updateChannel, desiredAppVersion: desiredAppVersion.trim() || null } : {}),
        }),
      });
      if (!r.ok) throw new Error(`HTTP ${r.status}: ${await r.text()}`);
      // 그룹 배정이 바뀌었으면 함께 저장한다(device_groups). 실패하면 폼은 남기고 이유를 보인다.
      const nextGroup = groupPick || null;
      if (groups && onAssignGroup && nextGroup !== (groupId ?? null)) {
        const ok = await onAssignGroup(nextGroup);
        if (!ok) throw new Error('그룹 배정을 저장하지 못했습니다. 권한을 확인해 주세요.');
      }
      toast(isNew ? '추가되었습니다.' : '저장되었습니다.', 'success');
      onSaved();
    } catch (e) {
      setError(describeApiError(e));
      setSaving(false);
    }
  };

  const inputCls = 'w-full px-3 py-2 bg-panel-2 border border-line rounded text-[14px] text-text';
  const labelCls = 'text-[13px] font-semibold text-text';
  // 라벨-입력 연결용 id. 한 화면에 폼이 하나뿐이라 접두어만으로 충분하다(검수 S16).
  const fid = (name: string): string => `device-edit-${name}`;

  return (
    <div className={cn('border border-line rounded-md bg-panel', className)}>
      <div className="flex items-center justify-between px-5 py-3 border-b border-line">
        <h2 className="text-[15px] font-semibold">디바이스 정보</h2>
      </div>

      <div className="px-5 py-5 flex flex-col gap-5">
        {error && <p className="font-mono text-[12px] text-red">{error}</p>}

        <div className="flex flex-col gap-1.5">
          <label className={labelCls} htmlFor={fid('id')}>디바이스 ID <span className="text-red">*</span></label>
          <div className="flex items-center gap-2">
            <input
              id={fid('id')}
              value={device.id}
              disabled
              className="min-w-0 flex-1 px-3 py-2 bg-panel-3 border border-line rounded text-[14px] text-text-dim font-mono cursor-not-allowed"
            />
            {/* 최고 관리자(H9·FO) — 하드웨어 교체용 재페어링. 바인딩·프로파일 유지. */}
            {isSuper && !isNew && (
              <Button variant="primary" onClick={() => setRenewOpen(true)}>
                디바이스 갱신
              </Button>
            )}
          </div>
        </div>

        <div className="flex flex-col gap-1.5">
          <label className={labelCls} htmlFor={fid('name')}>디바이스명 <span className="text-red">*</span></label>
          <input
            id={fid('name')}
            value={name}
            onChange={(e) => setName(e.target.value)}
            placeholder="디바이스명 입력"
            autoFocus
            className={inputCls}
          />
        </div>

        {/* 그룹 — 실제 배정(device_groups). 옛 자유입력 groupLabel은 없앴다(D2). */}
        {groups && (
          <div className="flex flex-col gap-1.5">
            <span className={labelCls}>그룹</span>
            <select aria-label="그룹" value={groupPick} onChange={(e) => setGroupPick(e.target.value)} className={inputCls}>
              <option value="">(미배정)</option>
              {groups.map((g) => <option key={g.id} value={g.id}>{g.name}</option>)}
              {groupId && !groups.some((g) => g.id === groupId) && <option value={groupId} disabled>현재 그룹 (조회 불가)</option>}
            </select>
            <p className="text-[12px] text-text-dim">그룹을 바꾸면 그 그룹의 관리자와 승인 방식이 적용됩니다.</p>
          </div>
        )}

        {/* 해상도 — 숫자만, 1회 지정 후 잠금. */}
        <div className="flex flex-col gap-1.5">
          <span className={labelCls} id={fid('res-label')}>해상도 <span className="text-red">*</span></span>
          <div className="flex items-center gap-2" role="group" aria-labelledby={fid('res-label')}>
            <input
              aria-label="가로 해상도"
              value={resW}
              onChange={(e) => setResW(e.target.value.replace(/\D/g, ''))}
              placeholder="W"
              disabled={resolutionLocked}
              className={`${inputCls} min-w-0 flex-1 ${resolutionLocked ? 'bg-panel-3 text-text-dim cursor-not-allowed' : ''}`}
            />
            <span className="text-text-dim">×</span>
            <input
              aria-label="세로 해상도"
              value={resH}
              onChange={(e) => setResH(e.target.value.replace(/\D/g, ''))}
              placeholder="H"
              disabled={resolutionLocked}
              className={`${inputCls} min-w-0 flex-1 ${resolutionLocked ? 'bg-panel-3 text-text-dim cursor-not-allowed' : ''}`}
            />
          </div>
          <p className="text-[12px] text-text-dim leading-relaxed [word-break:keep-all]">
            ⓘ 디바이스의 해상도를 불러올 수 있는 경우 자동 입력됩니다.
            <br />한번 지정한 해상도는 수정할 수 없습니다.
          </p>
        </div>

        {isBo && <div className="border-t border-line pt-4 flex flex-col gap-3">
          <span className={labelCls}>앱 배포</span>
          <select aria-label="업데이트 채널" value={updateChannel} onChange={(e) => setUpdateChannel(e.target.value as typeof updateChannel)} className={inputCls}>
            <option value="stable">정식 배포</option>
            <option value="canary">시험 배포</option>
            <option value="paused">업데이트 중지</option>
          </select>
          <input value={desiredAppVersion} onChange={(e) => setDesiredAppVersion(e.target.value)} placeholder="목표 버전(예: 0.4.7, 비우면 채널 최신)" className={inputCls} />
          <p className="text-[12px] text-text-dim">시험기 1대에서 확인한 뒤 정식 채널로 확대해 주세요.</p>
        </div>}

        {/* 캘리브레이션 — 지정 계정(+로컬 dev) 전용 노출. useCanCalibrate 참고. */}
        {canCalibrate && onCalibrate && (
          <div className="border-t border-line pt-4 flex flex-col gap-2">
            <p className="text-[13px] text-text-dim">
              디바이스가 곡면·모서리 등 평면이 아닌 곳에 설치되었다면 캘리브레이션 편집
              버튼을 선택해 화면을 보정하세요.
            </p>
            <div className="flex items-center gap-3">
              <Button onClick={onCalibrate}>
                캘리브레이션
              </Button>
              {/* ponytail: 캘리 편집 일시는 warp preset에 updatedAt이 노출되면 표시. */}
              <span className="text-[12px] text-text-dim">마지막 편집일 : -</span>
            </div>
          </div>
        )}
      </div>

      {/* 삭제는 목록 행이 아니라 여기, 수정 화면 맨 아래에 둔다 — 그룹 안 표에 있으면 '이 그룹에서
          빼기'로 읽히는데 실제로는 기기를 없앤다(2026-09-18 대교). 새로 추가하는 중에는 없다. */}
      {!isNew && onDeleted && (
        <div className="px-5 pb-1">
          <DangerZone title="위험 구역"
            description={`'${device.name}' 디바이스를 조직에서 삭제합니다.\n페어링이 풀려 기기에서 다시 등록해야 하고, 되돌릴 수 없습니다.`}
            {...(groups ? { hint: "다른 그룹으로 옮기려면 위 '그룹'을 바꾸고 저장하세요. 삭제할 필요가 없습니다." } : {})}
            actionLabel="디바이스 삭제"
            onAction={() => setDeleteOpen(true)} />
        </div>
      )}

      {/* 제출·취소는 항상 카드 발 오른쪽 — 머리에 두면 모달·단독 페이지에서 자리가 제각각이 된다(검수 S3). */}
      <div className="flex items-center justify-end gap-2 border-t border-line px-5 py-3.5">
        <Button onClick={() => setCancelOpen(true)}>취소</Button>
        <Button variant="primary" className="min-w-[88px] justify-center" disabled={!valid || saving} onClick={() => void save()}>
          {saving ? '처리 중…' : isNew ? '추가' : '저장'}
        </Button>
      </div>

      {renewOpen && (
        <PairingModal
          issueCode={async (): Promise<PairingCodeIssued> => {
            const r = await fetch(scoped(`${API_BASE}/api/devices/${device.id}/renew-code`), { method: 'POST' });
            if (!r.ok) throw new Error(`HTTP ${r.status}: ${await r.text()}`);
            renewIssuedAtRef.current = Date.now();
            return (await r.json()) as PairingCodeIssued;
          }}
          detectPaired={async () => {
            // 갱신은 같은 행의 토큰 회전이라 새 디바이스가 생기지 않는다 —
            // 발급 이후 lastSeenAt 갱신(새 하드웨어의 register/heartbeat)으로 감지.
            const r = await fetch(scoped(`${API_BASE}/api/devices/${device.id}`));
            if (!r.ok) return null;
            const d = (await r.json()) as Device;
            return d.lastSeenAt && new Date(d.lastSeenAt).getTime() > renewIssuedAtRef.current
              ? d.id
              : null;
          }}
          onClose={() => setRenewOpen(false)}
          onPaired={() => {
            setRenewOpen(false);
            toast('디바이스가 갱신되었습니다.', 'success');
            onSaved();
          }}
        />
      )}

      {deleteOpen && (
        <ConfirmModal
          title="디바이스 삭제"
          confirmLabel="삭제"
          message={`'${device.name}' 디바이스를 조직에서 삭제합니다.\n그룹에서만 빼는 것이 아니라 등록 자체가 사라지며, 되돌릴 수 없습니다.`}
          onCancel={() => setDeleteOpen(false)}
          onConfirm={async () => {
            const response = await fetch(scoped(`${API_BASE}/api/devices/${device.id}`), { method: 'DELETE' });
            if (!response.ok) throw new Error(`HTTP ${response.status}`);
            toast('삭제되었습니다.', 'success');
            setDeleteOpen(false);
            onDeleted?.();
          }}
        />
      )}

      {cancelOpen && (
        <ConfirmModal
          title="알림"
          message={'변경한 내용이 저장되지 않습니다.\n취소하시겠습니까?'}
          confirmLabel="확인"
          danger={false}
          onCancel={() => setCancelOpen(false)}
          onConfirm={() => { setCancelOpen(false); onCancel(); }}
        />
      )}
    </div>
  );
}

// 수정 페이지 — ?new=1 이면 '디바이스 추가'(페어링 직후 딥링크), 아니면 '디바이스 수정'.
function EditView({ device, onSaved, groups, groupId, onAssignGroup }: {
  device: Device; onSaved: () => void; groups: Array<{ id: string; name: string }>; groupId: string | null; onAssignGroup: (groupId: string | null) => Promise<boolean>;
}) {
  const search = useScopeSearch();
  const scoped = useScopedUrl();
  const navigate = useNavigate();
  const [params] = useSearchParams();
  const isNew = params.get('new') === '1';
  const leaf = isNew ? '디바이스 추가' : '디바이스 수정';
  return (
    <div className="relative h-full overflow-auto pb-10">
      {isNew ? <Breadcrumb leaf={leaf} /> : <Breadcrumb leaf={leaf} detailId={device.id} />}
      <DeviceEditForm className="mx-6 mt-4" device={device} isNew={isNew} groups={groups} groupId={groupId} onAssignGroup={onAssignGroup}
        onSaved={() => { onSaved(); navigate(`/devices/${device.id}${search}`, { replace: true }); }}
        onCancel={() => navigate(isNew ? '/org' : `/devices/${device.id}${search}`, { replace: true })}
        onCalibrate={() => navigate(`/devices/${device.id}/calibration${search}`)} />
    </div>
  );
}
